import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { User, KeyRound, Save } from "lucide-react";
import TwoFactorPanel from "../components/TwoFactorPanel";

/** Profile settings — visible on every role's dashboard. Lets the current user
 * update their name/phone and change their password (requires current password). */
export default function ProfileSettings() {
    const { user, setUser } = useAuth();
    const { t } = useI18n();
    const [profile, setProfile] = React.useState({ full_name: "", phone: "" });
    const [pwd, setPwd] = React.useState({ current_password: "", new_password: "", confirm: "" });
    const [savingProfile, setSavingProfile] = React.useState(false);
    const [savingPwd, setSavingPwd] = React.useState(false);

    React.useEffect(() => {
        if (user) setProfile({ full_name: user.full_name || "", phone: user.phone || "" });
    }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    async function saveProfile(e) {
        e.preventDefault();
        setSavingProfile(true);
        try {
            const { data } = await api.patch("/auth/profile", profile);
            setUser?.(prev => ({ ...(prev || {}), ...data }));
            toast.success("Profile updated");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        } finally { setSavingProfile(false); }
    }

    async function savePassword(e) {
        e.preventDefault();
        if (pwd.new_password.length < 8) return toast.error("New password must be at least 8 characters");
        if (pwd.new_password !== pwd.confirm) return toast.error("New passwords don't match");
        setSavingPwd(true);
        try {
            await api.post("/auth/change-password", {
                current_password: pwd.current_password,
                new_password: pwd.new_password,
            });
            toast.success("Password changed");
            setPwd({ current_password: "", new_password: "", confirm: "" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not change password");
        } finally { setSavingPwd(false); }
    }

    return (
        <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-settings-page">
            <div>
                <div className="overline text-brand-stone">{t("profile_account")}</div>
                <h1 className="font-serif text-3xl">{t("profile_title")}</h1>
            </div>

            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <User className="w-4 h-4 text-primary" />
                        <h2 className="font-dash text-lg font-semibold">{t("profile_your_details")}</h2>
                    </div>
                    <form onSubmit={saveProfile} className="space-y-4">
                        <div>
                            <Label>{t("email")}</Label>
                            <Input value={user?.email || ""} disabled data-testid="profile-email-input" className="mt-1.5 bg-secondary/40" />
                            <div className="text-[11px] text-brand-stone mt-1 italic">{t("profile_email_immutable")}</div>
                        </div>
                        <div>
                            <Label>{t("full_name")}</Label>
                            <Input
                                value={profile.full_name}
                                onChange={e => setProfile({ ...profile, full_name: e.target.value })}
                                data-testid="profile-name-input"
                                className="mt-1.5"
                                required
                            />
                        </div>
                        <div>
                            <Label>{t("phone")}</Label>
                            <Input
                                value={profile.phone}
                                onChange={e => setProfile({ ...profile, phone: e.target.value })}
                                data-testid="profile-phone-input"
                                placeholder="+1 555 123 4567"
                                className="mt-1.5"
                            />
                        </div>
                        <Button type="submit" disabled={savingProfile} data-testid="save-profile-btn" className="bg-primary hover:bg-primary/90 gap-2">
                            <Save className="w-4 h-4" /> {savingProfile ? t("add_child_saving") : t("profile_save")}
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <KeyRound className="w-4 h-4 text-primary" />
                        <h2 className="font-dash text-lg font-semibold">{t("profile_change_password")}</h2>
                    </div>
                    <form onSubmit={savePassword} className="space-y-4">
                        <div>
                            <Label>{t("profile_current_password")}</Label>
                            <Input
                                type="password"
                                value={pwd.current_password}
                                onChange={e => setPwd({ ...pwd, current_password: e.target.value })}
                                data-testid="current-password-input"
                                className="mt-1.5"
                                required
                            />
                        </div>
                        <div>
                            <Label>{t("profile_new_password")}</Label>
                            <Input
                                type="password"
                                value={pwd.new_password}
                                onChange={e => setPwd({ ...pwd, new_password: e.target.value })}
                                data-testid="new-password-input"
                                className="mt-1.5"
                                minLength={8}
                                required
                            />
                            <div className="text-[11px] text-brand-stone mt-1 italic">{t("profile_password_hint")}</div>
                        </div>
                        <div>
                            <Label>{t("profile_confirm_password")}</Label>
                            <Input
                                type="password"
                                value={pwd.confirm}
                                onChange={e => setPwd({ ...pwd, confirm: e.target.value })}
                                data-testid="confirm-password-input"
                                className="mt-1.5"
                                required
                            />
                        </div>
                        <Button type="submit" disabled={savingPwd} data-testid="change-password-btn" className="bg-primary hover:bg-primary/90 gap-2">
                            <KeyRound className="w-4 h-4" /> {savingPwd ? t("reset_updating") : t("profile_update_password")}
                        </Button>
                    </form>
                </CardContent>
            </Card>

            {(user?.role === "admin" || user?.role === "superadmin") && (
                <TwoFactorPanel />
            )}
        </div>
    );
}
